import { useState } from 'react';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import { getViewportPracticeId } from 'lib/helpers/viewportPath';
import { useCreateTag } from 'lib/hooks/queries/tags/useCreateTag';
import { VIEWPORT_PRIVATE_TAG_DISABLED_MESSAGE } from 'lib/constants/constants';

export const useTagCreateForm = ({ kind, name = '', addNewTagFromCreated, handleClose }) => {
  const [selectedColor, setSelectedColor] = useState(null);

  const initialValues = { kind, name, isPublic: true, color: '', isImportant: false };

  const { mutateAsync: mutateCreateTag, isLoading } = useCreateTag({
    kind,
    onSuccess: ({ tag }) => {
      addNewTagFromCreated?.({ ...tag, label: tag?.name, value: tag?.name });
      handleClose?.();
    }
  });

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      name: Yup.string().required('Tag name is required'),
      color: Yup.string().required('Tag color is required')
    }),
    onSubmit: (values) => mutateCreateTag({ ...values })
  });

  const resetTagForm = () => {
    setSelectedColor(null);
    formik.resetForm({ values: initialValues });
  };

  const handleChange = (key, value) => formik.setFieldValue(key, value);

  const handleSelectColor = (color) => {
    setSelectedColor(color);
    formik.setFieldValue('color', color);
  };

  // A private tag is readable only by the actor stored on it, and an admin in the
  // viewport is not a practice user — so a private one created here would be visible
  // to nobody. Public tags belong to the practice and are safe.
  const isPrivateBlocked = !!getViewportPracticeId();
  const privateBlockReason = isPrivateBlocked ? VIEWPORT_PRIVATE_TAG_DISABLED_MESSAGE : undefined;

  const isBlocked = isPrivateBlocked && !formik.values.isPublic;
  const blockReason = isBlocked ? privateBlockReason : undefined;

  const handleCreate = () => {
    if (isBlocked) return;
    formik.handleSubmit();
  };

  return {
    formik,
    selectedColor,
    isLoading,
    resetTagForm,
    handleChange,
    handleSelectColor,
    handleCreate,
    isBlocked,
    blockReason,
    isPrivateBlocked,
    privateBlockReason
  };
};
